// File: frontend/src/components/ui/FormField.js
//
// The one text input for forms. Wraps MUI TextField so every form gets the same
// label, required marker, and error/helper treatment instead of each screen
// re-implementing validation UX with inline styles.
//
//   <FormField
//     label="Phone number"
//     required
//     value={phone}
//     onChange={(e) => setPhone(e.target.value)}
//     error={errors.phone}          // string → shows as the error message
//     helperText="Format: 2567XXXXXXXX"
//   />
//
// Any MUI TextField prop (type, multiline, InputProps, select, …) passes through.

import React, { useId } from "react";
import { Box, TextField, useTheme } from "@mui/material";
import { TYPE_SCALE } from "./tokens";

// ── One input height, and text centred inside it ────────────────────────────
// MUI's default padding differs between a plain input, a select and an input
// carrying an adornment, so a stack of fields that are all nominally the same
// size still ends up with values sitting at slightly different heights and a
// ragged vertical rhythm down the form.
//
// Fixing the ROOT height and removing the vertical padding centres the value:
// a single-line <input> with no vertical padding centres its own text, and the
// select's inner div is told to do the same explicitly because it is a div and
// will not.
//
// Multiline is excluded on purpose. A textarea must grow, and its first line
// belongs at the TOP of the box — vertically centring a paragraph inside a
// growing field is exactly the wrong behaviour.
const HEIGHTS = { small: 44, medium: 52 };

const alignmentSx = (h) => ({
  // Everything except a textarea gets the fixed height.
  "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": {
    minHeight: h,
  },
  "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline) .MuiOutlinedInput-input": {
    height: h,
    paddingTop: 0,
    paddingBottom: 0,
    boxSizing: "border-box",
  },
  // The select renders a div, which does not centre its own text.
  "& .MuiSelect-select.MuiOutlinedInput-input": {
    display: "flex",
    alignItems: "center",
  },
  // Adornment icons sit on the same centre line as the value rather than on
  // the text baseline.
  "& .MuiInputAdornment-root": { height: "auto", alignSelf: "center" },
  // A textarea keeps real padding, and its label still needs clearance from
  // the border.
  "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
    paddingTop: 1.25,
    paddingBottom: 1.25,
  },
});

// ── The labelled field: label ABOVE, the box, then supporting text ──────────
// For forms where a field must be FOUND at a glance (the Wallet first). The
// label is a real <label> above the control, never a floating one sitting in
// the border, and the box itself clears WCAG 1.4.11's 3:1 against the surface
// it sits on. Colours are the theme's `palette.field` tokens, so every screen
// that opts in gets the same label, surface, boundary, focus and placeholder.
const labelledFieldSx = (theme) => {
  const f = theme.palette.field || {};
  return {
    "& .MuiOutlinedInput-root": { backgroundColor: f.surface },
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: f.border },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: f.borderHover },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main, borderWidth: 2,
    },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.error.main,
    },
    "& .MuiOutlinedInput-input::placeholder": { color: f.placeholder, opacity: 1 },
    "& .MuiFormHelperText-root": { marginLeft: 0, marginTop: 0.75, color: f.helper },
    "& .MuiFormHelperText-root.Mui-error": { color: theme.palette.error.main },
  };
};

export default function FormField({
  label,
  required = false,
  error,
  helperText,
  fullWidth = true,
  size = "small",
  // The labelled-field pattern above. Opt-in, so no other form changes.
  labelAbove = false,
  id: idProp,
  SelectProps,
  sx,
  ...props
}) {
  const height = HEIGHTS[size] || HEIGHTS.small;
  const autoId = useId();
  const theme = useTheme();

  if (!labelAbove) {
    return (
      <TextField
        label={label}
        required={required}
        error={Boolean(error)}
        helperText={error || helperText || undefined}
        fullWidth={fullWidth}
        size={size}
        id={idProp}
        SelectProps={SelectProps}
        // Caller styles win, so a field with a genuine reason to differ still can.
        sx={[alignmentSx(height), ...(Array.isArray(sx) ? sx : [sx])]}
        {...props}
      />
    );
  }

  const id = idProp || `ff-${autoId.replace(/:/g, "")}`;
  const labelId = `${id}-label`;
  return (
    <Box sx={{ width: fullWidth ? "100%" : undefined }}>
      <Box
        component="label"
        htmlFor={id}
        id={labelId}
        sx={{
          display: "block", mb: 0.75,
          fontSize: TYPE_SCALE.body, fontWeight: 600, lineHeight: 1.35,
          color: theme.palette.field?.label || "text.primary",
        }}
      >
        {label}
      </Box>
      <TextField
        id={id}
        required={required}
        error={Boolean(error)}
        helperText={error || helperText || undefined}
        fullWidth={fullWidth}
        size={size}
        // A select renders a div, not an input: name it by the same label.
        SelectProps={props.select ? { ...(SelectProps || {}), labelId } : SelectProps}
        sx={[alignmentSx(height), labelledFieldSx(theme), ...(Array.isArray(sx) ? sx : [sx])]}
        {...props}
      />
    </Box>
  );
}
